import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom"; // <- import navigate
import api from "../../services/api";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import MiniFooter from "../../components/Layout/miniFooter";

export default function AffiliatePage() {
  const navigate = useNavigate(); // <- initialize navigate
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(value || 0);

  const formatDate = (date) => new Date(date).toLocaleDateString();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/referrals/me");
      console.log('data: ', res.data)
      if (res.data.success) setData(res.data);
      else setError(res.data.message || "Failed to load affiliate data");
    } catch (err) {
      console.error(err);
      setError("Unable to load affiliate data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert("Unable to copy referral link");
    }
  };

  const cardStyle = {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ padding: "32px", textAlign: "center", minHeight: "60vh" }}>
          <p>Loading affiliate dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div style={{ padding: "32px", textAlign: "center", minHeight: "60vh" }}>
          <p>{error}</p>
          <button
            onClick={fetchData}
            style={{ padding: "10px 16px", borderRadius: "8px", background: "#3B82F6", color: "#fff" }}
          >
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // Not an affiliate yet -> redirect to register page
  if (!data?.referralCode) {
    return (
      <DashboardLayout>
        <div style={{ padding: "32px", maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "12px" }}>Affiliate Program</h2>
          <p style={{ color: "#6B7280", marginBottom: "24px" }}>
            Invite users to the platform and earn commission when they upgrade.
          </p>
          <button
            onClick={() => navigate("/affiliate/register")} // <- redirect to register page
            style={{
              padding: "12px 20px",
              borderRadius: "8px",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Join Affiliate Program
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div style={{ padding: "32px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <header style={{ textAlign: "center", marginBottom: "32px" }}>
          <h2 style={{ fontSize: "28px", fontWeight: "700", marginBottom: "6px" }}>Affiliate Dashboard</h2>
          <p style={{ color: "#6B7280" }}>Track referrals, earnings, and withdrawals.</p>
        </header>

        {/* Referral Link */}
        <section style={cardStyle}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "12px" }}>Your Referral Link</h3>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <input
              value={data.referralLink}
              readOnly
              style={{ flex: 1, padding: "10px 12px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
            />
            <button
              onClick={copyLink}
              style={{ padding: "10px 16px", borderRadius: "8px", background: "#3B82F6", color: "#fff", cursor: "pointer", border : "none" }}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p style={{ color: "#6B7280" }}>
            Code: <strong>{data.referralCode}</strong>
          </p>
        </section>

        {/* Stats */}
        <section style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "32px" }}>
          {[
            { label: "Total Referrals", value: data.referralsCount },
            { label: "Total Earnings", value: formatCurrency(data.totalEarnings) },
            { label: "Available Balance", value: formatCurrency(data.availableBalance) },
          ].map((stat) => (
            <div key={stat.label} style={{ ...cardStyle, flex: "1 1 250px", textAlign: "center" }}>
              <h3 style={{ fontSize: "22px", fontWeight: "600", marginBottom: "6px" }}>{stat.value}</h3>
              <p style={{ color: "#6B7280" }}>{stat.label}</p>
            </div>
          ))}
        </section>

        {/* Withdraw */}
        <section style={cardStyle}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "12px" }}>Withdraw Earnings</h3>
          {data.canWithdraw ? (
            <button style={{ padding: "12px 20px", borderRadius: "8px", background: "#10B981", color: "#fff", fontWeight: "600" }}>
              Withdraw Funds
            </button>
          ) : (
            <p style={{ color: "#6B7280" }}>{data.withdrawalMessage}</p>
          )}
        </section>

        {/* Referral History */}
        <section style={cardStyle}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "12px" }}>Referral History</h3>
          {data.referrals?.length === 0 ? (
            <p>No referrals yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #E5E7EB", textAlign: "left" }}>
                    <th style={{ padding: "8px" }}>Name</th>
                    <th style={{ padding: "8px" }}>Email</th>
                    <th style={{ padding: "8px" }}>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrals.map((r) => (
                    <tr key={r._id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                      <td style={{ padding: "8px" }}>{r.refereeUserId?.fullName}</td>
                      <td style={{ padding: "8px" }}>{r.refereeUserId?.email}</td>
                      <td style={{ padding: "8px" }}>{formatDate(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
              <MiniFooter />
    </DashboardLayout>
  );
}